/* eslint-disable import/prefer-default-export */
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { trackRudderstackImageUploadError } from "common/utils/rudderstack/tracker";
import {
  emitTransferUploadParallelEvent,
  isTransferBusinessLine
} from "common/utils/transfer-upload/parallel-emitter";
import { BUSINESS_LINE } from "common/utils/app-registry/contants";
import { getAcceptString, materializeFile, validateArtworkFileType } from "./artwork-file-types";

/**
 * Checks if the file type is supported for printing.
 *
 * @param {File} file - The file to be checked.
 * @param {boolean} printAsIs - Indicates whether to print the file as is (gangsheet) or not (artwork).
 * @returns {Object} - An object containing `valid` (boolean) and `message` (string) properties.
 */
export const isFileTypeSupported = (file, printAsIs) => {
  const fileChecker = printAsIs ? gangsheetFileChecker : validateArtworkFileType;

  return fileChecker(file);
};

/**
 * Validates file type and materializes the file for upload.
 * Handles files from cloud storage providers (Google Drive, Dropbox) on mobile
 * by reading file content into memory before upload.
 *
 * @param {File} file - The file to validate and materialize.
 * @param {boolean} printAsIs - Indicates whether to print the file as is (gangsheet) or not (artwork).
 * @returns {Promise<{ valid: boolean, message: string, errorType?: string, file?: File }>} - Validation result with materialized file.
 */
export const validateAndMaterializeFile = async (file, printAsIs) => {
  // First check file type (quick check, no file read needed)
  const typeValidation = isFileTypeSupported(file, printAsIs);
  if (!typeValidation.valid) {
    return { ...typeValidation, errorType: "unsupported_type" };
  }

  // Check file size before reading the file (must match backend DtfArtwork constants)
  const maxSizeBytes = printAsIs ? 500 * 1024 * 1024 : 40 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    const maxMb = maxSizeBytes / (1024 * 1024);
    return {
      valid: false,
      message: `File is too large. Maximum allowed size is ${maxMb} MB.`,
      errorType: "file_too_large"
    };
  }

  // Materialize the file to handle cloud storage providers on mobile
  // This ensures the file content is actually available before upload
  const { file: materializedFile, error: materializeError } = await materializeFile(file);
  if (materializeError || !materializedFile) {
    return {
      valid: false,
      message: materializeError || "Failed to read file",
      errorType: "materialization_error"
    };
  }

  // Re-check size after materialization — cloud-provider files (Google Drive, Dropbox)
  // on mobile may report file.size as 0 or a stub value before content is downloaded
  if (materializedFile.size > maxSizeBytes) {
    const maxMb = maxSizeBytes / (1024 * 1024);
    return {
      valid: false,
      message: `File is too large. Maximum allowed size is ${maxMb} MB.`,
      errorType: "file_too_large"
    };
  }

  // Return the materialized file so the caller can use it for upload
  return { valid: true, message: "", file: materializedFile };
};

/* eslint-enable import/prefer-default-export */

const gangsheetFileChecker = ({ type, name }) => {
  // Check MIME type first, then fallback to extension for Android cloud provider files
  let valid = type === "image/png";

  if (!valid && !type && name) {
    const extension = name.toLowerCase().match(/\.[^.]+$/)?.[0];
    valid = extension === ".png";
  }

  const message = valid ? "" : "PNG file is required for gang sheet orders.";

  return { valid, message };
};

/**
 * Returns the accepted file types based on the given printAsIs flag.
 *
 * @param {boolean} printAsIs - Indicates whether to print the file as is (gangsheet) or not (artwork).
 * @returns {string} - The accepted file types.
 */
export const acceptedFileTypes = printAsIs => (printAsIs ? "image/png" : getAcceptString());

/* eslint-disable max-params, max-lines-per-function */
export const logUnsupportedFileTypeEvent = (
  file,
  otherData,
  errorMessage,
  errorType = "unsupported_file_type"
) => {
  const safeMessage =
    typeof errorMessage === "string" && errorMessage.trim().length > 0
      ? errorMessage
      : "Unsupported file type";

  submitDtfEvent("image_upload_unsupported_file", {
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    ...otherData
  });
  trackRudderstackImageUploadError({
    ...otherData,
    error_message: safeMessage,
    error_type: errorType,
    file_name: file.name,
    file_type: file.type,
    file_size: file.size
  });

  // MN-4800: dual-emit the new transfer_upload_validation_friction event.
  // Every caller of this helper is a transfer-pipeline surface (verified by
  // grep at commit time), so default the business_line gate to Transfers
  // when caller didn't pass it through otherData.
  const businessLine = otherData?.business_line ?? BUSINESS_LINE.DTF;
  if (!isTransferBusinessLine(businessLine)) return;

  emitTransferUploadParallelEvent({
    legacyEvent: "image_upload_unsupported_file",
    errorContext: {
      error_type: otherData?.error_type ?? "unsupported_file",
      error_message: errorMessage,
      business_line: businessLine,
      page: otherData?.page,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      upload_uuid: file?.uploadUuid ?? otherData?.upload_uuid
    }
  });
};
/* eslint-enable max-params, max-lines-per-function */
